import { useMemo } from "react";
import dayjs from "dayjs";
import { flightFormValuesSchema } from "@/app/ui/flights/validation";
import { hotelFormValuesSchema } from "@/app/ui/hotels/validation";
import { useValidatedLocalStorage } from "./useValidatedLocalStorage";
import type { TFlightFormValues, THotelFormValues } from "../types/types";
import { initialHotelsValues } from "../initialHotelsValues";
import { initialFlightsValues } from "../initialFlightsValues";
import { useTranslations } from "use-intl";

export const HOTELS_SEARCH_DATA_STORAGE_KEY = "tz_previousHotelSearchData@2";
export const FLIGHTS_SEARCH_DATA_STORAGE_KEY = "tz_previousFlightSearchData@2";

const createTravelDates = (startDate: Date, endDate: Date) => {
  // if dates are in the past, create new ones based on travel duration
  if (dayjs(startDate).isBefore(new Date(), "day")) {
    const duration = dayjs(endDate).diff(startDate, "day") || 1;
    return [new Date(), dayjs().add(duration, "day").toDate()];
  }

  // if dates are valid, use them
  return [startDate, endDate];
};

export const usePreviousHotelSearchData = () => {
  const [previousSearchData, setPreviousSearchData] =
    useValidatedLocalStorage<THotelFormValues>(
      HOTELS_SEARCH_DATA_STORAGE_KEY,
      initialHotelsValues,
      hotelFormValuesSchema,
    );

  const normalizedSearchData = useMemo(() => {
    const searchData = previousSearchData || initialHotelsValues;

    const [checkIn, checkOut] = createTravelDates(
      searchData.dates.checkIn,
      searchData.dates.checkOut,
    );

    return {
      ...searchData,
      dates: {
        checkIn,
        checkOut,
      },
    };
  }, [previousSearchData]);

  return {
    previousSearchData: normalizedSearchData,
    setPreviousSearchData,
  };
};

export const usePreviousFlightSearchData = () => {
  const t = useTranslations();

  const initialValues = useMemo(() => {
    const defaultDeparture = {
      id: "1",
      name: t("searchRecommendations.baghdad"),
      code: "BGW",
      countryName: t("searchRecommendations.iraq"),
      countryCode: "IQ",
      type: "CITY" as const,
    };

    return { ...initialFlightsValues, from: defaultDeparture };
  }, [t]);

  const [previousSearchData, setPreviousSearchData] =
    useValidatedLocalStorage<TFlightFormValues>(
      FLIGHTS_SEARCH_DATA_STORAGE_KEY,
      initialValues,
      flightFormValuesSchema,
    );

  const normalizedSearchData = useMemo(() => {
    const searchData = previousSearchData || initialValues;

    const [departure, returnDate] = createTravelDates(
      searchData.dates.departure,
      searchData.dates.return,
    );

    return {
      ...searchData,
      dates: {
        departure,
        return: returnDate,
      },
    };
  }, [initialValues, previousSearchData]);

  return {
    previousSearchData: normalizedSearchData,
    setPreviousSearchData,
  };
};
